import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import useRetryFetch from "./useRetryFetch";
import { getSeed, keepFlow } from "../api/start";
import { clearFlow, clearPendingKeep, hasPendingKeep, loadFlow, normalizeLocale } from "../lib/startFlow";
import { buildKeepBody } from "../lib/startKeep";
import { trackStart } from "../lib/startAnalytics";

/**
 * After "Keep this" the visitor signs up or logs in (email or Google: both
 * redirect within this tab, so sessionStorage survives). Once logged in, post
 * the pending flow exactly once.
 */
export default function useStartPendingKeep(isLoggedIn: boolean) {
  const retryFetch = useRetryFetch();
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const running = useRef(false);

  // Re-checks on every navigation: a visitor who is already logged in clicks
  // "Keep this" and is navigated to /home without isLoggedIn changing.
  useEffect(() => {
    if (!isLoggedIn || running.current || !hasPendingKeep()) return;
    const flow = loadFlow();
    if (!flow || !flow.seedId) {
      clearPendingKeep();
      return;
    }
    running.current = true;
    const locale = normalizeLocale(i18n.language);
    (async () => {
      try {
        const seed = await getSeed(flow.seedId!, locale);
        const body = buildKeepBody(flow, seed.text, locale);
        if (!body) throw new Error("no keep body");
        await keepFlow(retryFetch, body);
        trackStart("start_kept", { seedId: flow.seedId, angleId: body.angleId, src: flow.src, c: flow.c, step: !!flow.step });
        clearFlow();
        toast.success(t("startFlow.keptToast"));
      } catch {
        toast.error(t("startFlow.error"));
      } finally {
        clearPendingKeep();
        running.current = false;
      }
    })();
  }, [isLoggedIn, pathname, retryFetch, t, i18n.language]);
}
